import SelectorList from '@components/tracker-management/selector-list/SelectorList';
import SelectorModal from '@components/tracker-management/selector-modal/SelectorModal';
import TrackerHeader from '@components/tracker-management/tracker-header/TrackerHeader';
import TrackerSettings from '@components/tracker-management/tracker-settings/TrackerSettings';
import { useGetUserSelectedSite } from '@hooks/index';
import {
    getProject,
    getProjectSelectors,
    regenerateTracker,
    updateProject,
} from '@services/tracker/tracker.service';
import { ProjectResponse, SelectorResponse } from '@shared/interfaces/api/trackerRequest';
import { useEffect, useState } from 'react';
import './ProjectManagementSettings.sass';

export default function ProjectManagementSettings() {
    const selectedSite = useGetUserSelectedSite();
    // Use clientId as project identifier (matches site.name on backend)
    const projectSlug = selectedSite?.clientId;
    
    const [project, setProject] = useState<ProjectResponse | null>(null);
    const [selectors, setSelectors] = useState<SelectorResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSelector, setEditingSelector] = useState<SelectorResponse | null>(null);

    useEffect(() => {
        if (projectSlug) {
            loadData();
        } else {
            setIsLoading(false);
            setError('No project slug available for the selected site');
        }
    }, [projectSlug]);

    const loadData = async () => {
        if (!projectSlug) return;

        setIsLoading(true);
        setError('');

        const [projectResponse, selectorsResponse] = await Promise.all([
            getProject(projectSlug),
            getProjectSelectors(projectSlug),
        ]);

        if (projectResponse.error) {
            setError(projectResponse.error);
            setIsLoading(false);
            return;
        }

        if (selectorsResponse.error) {
            setError(selectorsResponse.error);
            setIsLoading(false);
            return;
        }

        setProject(projectResponse.data);
        setSelectors(selectorsResponse.data || []);
        setIsLoading(false);
    };

    const handleRegenerateTracker = async () => {
        if (!projectSlug) return;

        setIsRegenerating(true);
        setError('');

        const response = await regenerateTracker(projectSlug);

        if (response.error) {
            setError(response.error);
            setIsRegenerating(false);
            return;
        }

        setProject(response.data);
        setIsRegenerating(false);
    };

    const handleAddSelector = () => {
        setEditingSelector(null);
        setIsModalOpen(true);
    };

    const handleEditSelector = (selector: SelectorResponse) => {
        setEditingSelector(selector);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingSelector(null);
    };

    const handleSelectorSaved = () => {
        setIsModalOpen(false);
        setEditingSelector(null);
        loadData();
    };

    const handleSettingsChange = async (settings: { track_all_links?: boolean; track_all_buttons?: boolean }) => {
        if (!projectSlug) return;

        const response = await updateProject(projectSlug, settings);
        if (response.error) {
            setError(response.error);
            throw new Error(response.error);
        }

        if (response.data) {
            setProject(response.data);
        }
    };

    if (isLoading) {
        return (
            <div className="project-management-settings">
                <div className="loading">Loading...</div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="project-management-settings">
                <div className="error">Project not found</div>
            </div>
        );
    }

    return (
        <div className="project-management-settings">
            <TrackerHeader
                project={project}
                onRegenerateTracker={handleRegenerateTracker}
                isRegenerating={isRegenerating}
            />

            {error && <div className="error-message">{error}</div>}

            <TrackerSettings
                project={project}
                onSettingsChange={handleSettingsChange}
            />

            <SelectorList
                selectors={selectors}
                onAddSelector={handleAddSelector}
                onEditSelector={handleEditSelector}
                onSelectorDeleted={loadData}
                projectId={projectSlug}
            />

            {isModalOpen && (
                <SelectorModal
                    projectId={projectSlug}
                    selector={editingSelector}
                    onClose={handleModalClose}
                    onSaved={handleSelectorSaved}
                />
            )}
        </div>
    );
}
